import { useState, useEffect, useCallback } from 'react';
import {
  getConsentState,
  hasConsentDecision,
  acceptAllCookies,
  acceptEssentialOnly,
  saveConsentState,
  type CookieConsentState,
  type ConsentCategory,
} from '@/lib/cookieManager';

interface UseCookieConsentReturn {
  /** Whether the consent banner should be shown */
  showBanner: boolean;
  /** Current consent state */
  consent: CookieConsentState | null;
  /** Accept all cookies */
  acceptAll: () => void;
  /** Accept only essential cookies */
  acceptEssential: () => void;
  /** Save custom consent preferences */
  savePreferences: (preferences: Partial<CookieConsentState>) => void;
  /** Check if a specific category is consented */
  hasConsent: (category: ConsentCategory) => boolean;
  /** Dismiss the banner (user can still change preferences later) */
  dismissBanner: () => void;
}

export function useCookieConsent(): UseCookieConsentReturn {
  const [showBanner, setShowBanner] = useState(false);
  const [consent, setConsent] = useState<CookieConsentState | null>(null);

  // Check consent state on mount
  useEffect(() => {
    const hasDecision = hasConsentDecision();
    setShowBanner(!hasDecision);
    setConsent(getConsentState());
  }, []);

  const acceptAll = useCallback(() => {
    acceptAllCookies();
    setConsent(getConsentState());
    setShowBanner(false);
  }, []);

  const acceptEssential = useCallback(() => {
    acceptEssentialOnly();
    setConsent(getConsentState());
    setShowBanner(false);
  }, []);

  const savePreferences = useCallback((preferences: Partial<CookieConsentState>) => {
    saveConsentState(preferences);
    setConsent(getConsentState());
    setShowBanner(false);
  }, []);

  const hasConsentForCategory = useCallback((category: ConsentCategory): boolean => {
    if (category === 'essential') return true;
    return consent?.[category] === true;
  }, [consent]);

  const dismissBanner = useCallback(() => {
    // Accept essential only when dismissing without explicit choice
    acceptEssentialOnly();
    setConsent(getConsentState());
    setShowBanner(false);
  }, []);

  return {
    showBanner,
    consent,
    acceptAll,
    acceptEssential,
    savePreferences,
    hasConsent: hasConsentForCategory,
    dismissBanner,
  };
}
